import { Nullable } from "next-usequerystate";

import { CalendarDate } from "@puzzle/utils";
import { RangePreset, RangePresets } from "@puzzle/ui";

import { TransactionSortOrder, IntegrationType, RecurrencePeriod } from "graphql/types";

import { DEFAULT_PAGE_SIZE } from "components/dashboard/Transactions/constants";

import { TRANSACTIONS_PAGE_SIZE } from "constants/localStorageKeys";

export type PageTotals = {
  count: number;
  gain: number;
  loss: number;
  sum: number;
  assignedToMeCount: number;
};

export enum CashFlowFriendly {
  MoneyIn = "in",
  MoneyOut = "out",
}

export enum FilterStatus {
  Finalized = "finalized",
  NotCategorized = "uncategorized",
  NotFinalized = "unfinalized",
  NotLinkedBankTransfers = "NotLinkedBankTransfers",
  NotLinkedCCPayments = "NotLinkedCCPayments",
  NotLinkedProcessorTransfers = "NotLinkedProcessorTransfers",
}

export const DEFAULT_RANGE_PRESET = RangePresets.Last90Days;
export const DEFAULT_RANGE = DEFAULT_RANGE_PRESET.range!();

export type ClassFilter = {
  withSomeOfClassSegmentIds: string[];
  withNoneOfClassIds: string[];
};
export const DEFAULT_CLASS_FILTERS: ClassFilter = {
  withSomeOfClassSegmentIds: [],
  withNoneOfClassIds: [],
};

export type QueryFilterState = {
  accountIds: string[];
  accuracyCheckId: string;
  assignedToMe: boolean;
  assignedToOthers: boolean;
  cardIds: string[];
  cashFlow: CashFlowFriendly;
  categorizedByAI: boolean;
  classesFilter: ClassFilter;
  descriptor: string;
  descriptorExact: string;
  end: CalendarDate;
  hasDocumentation: boolean;
  hasSplits: boolean;
  highPriority: boolean;
  integrationTypes: IntegrationType[];
  isBillPayment: boolean;
  // NOTE: isLinked being true doesn't work as expected.
  // It only works for false/null
  isLinked: boolean;
  isManual: boolean;
  ledgerAccountDisplayIds: string[];
  ledgerCoaKeys: string[];
  maxAmount: number;
  minAmount: number;
  pageSize: number;
  posted: boolean;
  rangePreset: RangePreset;
  recurrences: RecurrencePeriod[];
  showAccrualDate: boolean;
  showAvailableDate: boolean;
  sortBy: TransactionSortOrder;
  start: CalendarDate;
  status: FilterStatus;
  vendorIds: string[];
};

export type OptionalFields =
  | "rangePreset"
  | "cashFlow"
  | "minAmount"
  | "maxAmount"
  | "status"
  | "assignedToMe"
  | "assignedToOthers"
  | "posted"
  | "hasDocumentation"
  | "isManual"
  | "showAccrualDate"
  | "showAvailableDate"
  | "recurrences"
  | "categorizedByAI"
  | "isLinked"
  | "isBillPayment"
  | "hasSplits"
  | "descriptorExact"
  | "highPriority"
  | "integrationTypes"
  | "accuracyCheckId";

export type FilterState = Omit<QueryFilterState, OptionalFields | "sortBy"> &
  Nullable<Pick<QueryFilterState, OptionalFields>>;

const getStoredPageSize = () => {
  if (typeof window === "undefined") {
    return DEFAULT_PAGE_SIZE; // Return default value during SSR
  }
  const stored = localStorage.getItem(TRANSACTIONS_PAGE_SIZE);
  return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
};

export const defaultFilterState: FilterState = {
  accountIds: [],
  accuracyCheckId: null,
  assignedToMe: null,
  assignedToOthers: null,
  cardIds: [],
  cashFlow: null,
  categorizedByAI: null,
  classesFilter: DEFAULT_CLASS_FILTERS,
  descriptor: "",
  descriptorExact: null,
  end: DEFAULT_RANGE[1],
  hasDocumentation: null,
  hasSplits: null,
  highPriority: null,
  integrationTypes: null,
  isBillPayment: null,
  // NOTE: isLinked being true doesn't work as expected.
  // It only works for false/null
  isLinked: null,
  isManual: null,
  ledgerAccountDisplayIds: [],
  ledgerCoaKeys: [],
  maxAmount: null,
  minAmount: null,
  pageSize: getStoredPageSize(),
  posted: null,
  rangePreset: DEFAULT_RANGE_PRESET,
  recurrences: [],
  showAccrualDate: null,
  showAvailableDate: null,
  start: DEFAULT_RANGE[0],
  status: null,
  vendorIds: [],
};
